import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { DemoVideo, getDemoVideos, toYouTubeEmbedUrl } from "../api/SettingsAPI";

/** The configured demo videos; null while loading, [] when none. */
export function useDemoVideos(): DemoVideo[] | null {
    const [videos, setVideos] = useState<DemoVideo[] | null>(null);
    useEffect(() => {
        getDemoVideos()
            .then(setVideos)
            .catch(() => setVideos([])); // absence of demos must never break the app
    }, []);
    return videos;
}

/** First demo video as an embed URL; null while loading / when none.
 *  Kept for the menu / landing page, which only need "is there a demo". */
export function useDemoEmbedUrl(): string | null {
    const videos = useDemoVideos();
    const first = videos?.find((v) => toYouTubeEmbedUrl(v.url));
    return first ? toYouTubeEmbedUrl(first.url) : null;
}

/** Public /demo page: plays the site's demo videos, in order. A slug in the
 *  URL (/demo/home_rental) narrows the page to that one video; an unknown
 *  slug falls back to the full list. */
const DemoPage: React.FC = () => {
    const { slug } = useParams<{ slug?: string }>();
    const videos = useDemoVideos();
    const all = (videos ?? [])
        .map((v) => ({ title: v.title, slug: v.slug ?? undefined, embedUrl: toYouTubeEmbedUrl(v.url) }))
        .filter((v): v is { title: string; slug: string | undefined; embedUrl: string } => !!v.embedUrl);
    const matched = slug ? all.filter((v) => v.slug === slug) : [];
    const playable = matched.length > 0 ? matched : all;

    return (
        <div className="app-layout">
            <div className="public-topbar">
                <strong className="public-brand">BabaHanks</strong>
                <div className="public-links">
                    <Link to="/" className="btn-outline">Back to Search</Link>
                </div>
            </div>
            <div className="public-body">
                <div style={{ width: "100%", maxWidth: "960px", margin: "1rem auto" }}>
                    <h2 style={{ textAlign: "center" }}>Demo</h2>
                    {matched.length > 0 && all.length > matched.length && (
                        <p style={{ textAlign: "center" }}>
                            <Link to="/demo">See all demos</Link>
                        </p>
                    )}
                    {playable.length > 0 ? (
                        playable.map((v, i) => (
                            <div key={`${v.embedUrl}-${i}`} style={{ marginBottom: "2rem" }}>
                                {v.title && (
                                    <h3 style={{ margin: "0 0 0.5rem" }}>{v.title}</h3>
                                )}
                                <div style={{ position: "relative", paddingTop: "56.25%" }}>
                                    <iframe
                                        src={v.embedUrl}
                                        title={v.title || `Demo video ${i + 1}`}
                                        style={{
                                            position: "absolute",
                                            inset: 0,
                                            width: "100%",
                                            height: "100%",
                                            border: 0,
                                            borderRadius: "8px",
                                        }}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                    />
                                </div>
                            </div>
                        ))
                    ) : videos !== null ? (
                        <p style={{ textAlign: "center", color: "var(--text-secondary)" }}>
                            No demo video is available right now.
                        </p>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default DemoPage;